import type { BlockNode } from './types';

/**
 * Recursively searches a BlockNode tree for a node matching the specified id.
 */
export function findNodeById(root: BlockNode, id: string): BlockNode | null {
  if (!root || !id) return null;
  if (root.id === id) return root;

  if (root.children && Array.isArray(root.children)) {
    for (const child of root.children) {
      const found = findNodeById(child, id);
      if (found) return found;
    }
  }

  return null;
}

/**
 * Recursively searches for the parent of a node with targetId.
 */
export function findParentNode(root: BlockNode, targetId: string): BlockNode | null {
  if (!root || !root.children) return null;

  for (const child of root.children) {
    if (child.id === targetId) return root;
    const found = findParentNode(child, targetId);
    if (found) return found;
  }

  return null;
}

/**
 * Returns an array of BlockNodes from root down to the target node (used for breadcrumbs).
 */
export function findPathToNode(root: BlockNode, targetId: string): BlockNode[] {
  if (!root || !targetId) return [];
  if (root.id === targetId) return [root];

  if (root.children && Array.isArray(root.children)) {
    for (const child of root.children) {
      const subPath = findPathToNode(child, targetId);
      if (subPath.length > 0) {
        return [root, ...subPath];
      }
    }
  }

  return [];
}

/**
 * Deep clones a BlockNode and assigns brand new unique IDs recursively.
 */
export function cloneNodeWithNewIds(node: BlockNode): BlockNode {
  const randomSuffix = Math.random().toString(36).substring(2, 7);
  const newId = `${node.type.toLowerCase()}-${Date.now().toString(36)}-${randomSuffix}`;

  return {
    ...node,
    id: newId,
    props: JSON.parse(JSON.stringify(node.props || {})),
    styles: JSON.parse(JSON.stringify(node.styles || { base: {} })),
    children: node.children ? node.children.map(cloneNodeWithNewIds) : undefined,
  };
}

/**
 * Inserts a node into targetParent at the specified index, or appends at the end.
 */
export function insertNodeAtIndex(
  root: BlockNode,
  targetParentId: string,
  nodeToInsert: BlockNode,
  index?: number
): BlockNode {
  if (root.id === targetParentId) {
    const children = root.children ? [...root.children] : [];
    if (index === undefined || index < 0 || index >= children.length) {
      children.push(nodeToInsert);
    } else {
      children.splice(index, 0, nodeToInsert);
    }
    return { ...root, children };
  }

  if (!root.children) return root;

  return {
    ...root,
    children: root.children.map((child) =>
      insertNodeAtIndex(child, targetParentId, nodeToInsert, index)
    ),
  };
}

/**
 * Moves a node up or down among its siblings.
 */
export function moveNodeDirection(
  root: BlockNode,
  targetId: string,
  direction: 'up' | 'down'
): BlockNode {
  const parent = findParentNode(root, targetId);
  if (!parent || !parent.children) return root;

  const currentIndex = parent.children.findIndex((c) => c.id === targetId);
  if (currentIndex === -1) return root;

  const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
  if (targetIndex < 0 || targetIndex >= parent.children.length) return root;

  const newChildren = [...parent.children];
  const [removed] = newChildren.splice(currentIndex, 1);
  newChildren.splice(targetIndex, 0, removed);

  return updateNodeDirect(root, parent.id, (p) => ({
    ...p,
    children: newChildren,
  }));
}

/**
 * Moves a node from its current position to a target parent at a specified index.
 */
export function moveNodeToParent(
  root: BlockNode,
  targetId: string,
  destParentId: string,
  destIndex: number
): BlockNode {
  const node = findNodeById(root, targetId);
  if (!node) return root;
  if (targetId === destParentId) return root; // Cannot move into oneself

  // 1. Remove node from root
  const rootWithoutNode = removeNodeFromTree(root, targetId);

  // 2. Insert into destination parent
  return insertNodeAtIndex(rootWithoutNode, destParentId, node, destIndex);
}

/**
 * Duplicates a node and inserts the clone immediately after the original node.
 */
export function duplicateNodeInTree(
  root: BlockNode,
  targetId: string
): { newRoot: BlockNode; duplicatedId: string | null } {
  const node = findNodeById(root, targetId);
  if (!node) return { newRoot: root, duplicatedId: null };

  const parent = findParentNode(root, targetId);
  const clone = cloneNodeWithNewIds(node);

  if (!parent) {
    // Cannot duplicate root
    return { newRoot: root, duplicatedId: null };
  }

  const currentIndex = parent.children?.findIndex((c) => c.id === targetId) ?? -1;
  const insertIndex = currentIndex >= 0 ? currentIndex + 1 : undefined;

  const newRoot = insertNodeAtIndex(root, parent.id, clone, insertIndex);
  return { newRoot, duplicatedId: clone.id };
}

/**
 * Wraps a node inside a new Container block.
 */
export function wrapNodeInContainer(
  root: BlockNode,
  targetId: string
): { newRoot: BlockNode; containerId: string } {
  const node = findNodeById(root, targetId);
  const parent = findParentNode(root, targetId);
  if (!node || !parent || !parent.children) {
    return { newRoot: root, containerId: '' };
  }

  const containerId = `container-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 5)}`;
  const containerNode: BlockNode = {
    id: containerId,
    type: 'Container',
    category: 'layout',
    props: {},
    styles: { base: { padding: '16px' } },
    children: [node],
  };

  const currentIndex = parent.children.findIndex((c) => c.id === targetId);
  const newChildren = [...parent.children];
  newChildren.splice(currentIndex, 1, containerNode);

  const newRoot = updateNodeDirect(root, parent.id, (p) => ({
    ...p,
    children: newChildren,
  }));

  return { newRoot, containerId };
}

/**
 * Helper to update a node in place.
 */
function updateNodeDirect(
  root: BlockNode,
  targetId: string,
  transform: (node: BlockNode) => BlockNode
): BlockNode {
  if (root.id === targetId) {
    return transform(root);
  }
  if (!root.children) return root;

  return {
    ...root,
    children: root.children.map((child) => updateNodeDirect(child, targetId, transform)),
  };
}

/**
 * Recursively removes a node by id.
 */
export function removeNodeFromTree(root: BlockNode, targetId: string): BlockNode {
  if (!root.children) return root;

  return {
    ...root,
    children: root.children
      .filter((child) => child.id !== targetId)
      .map((child) => removeNodeFromTree(child, targetId)),
  };
}

export * from './utils/siteChrome';

